import React, { useCallback, useEffect, useState, useRef, Component } from 'react';
import { XIcon, EditIcon, Trash2Icon, CalendarIcon, ClockIcon, MapPinIcon, FileTextIcon, AlertCircleIcon, MessageCircleIcon, AlertTriangleIcon } from 'lucide-react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { DurationPickerDropdown } from './DurationPickerDropdown';
import { TimePickerDropdown } from './TimePickerDropdown';
import { CustomDropdown } from './CustomDropdown';
import { CancelLessonModal } from './modals/CancelLessonModal';
import { useAuth } from '../contexts/AuthContext';
export interface LessonDetails {
  id: string | number;
  title: string;
  studentName: string;
  instrument: string;
  date: string;
  time: string;
  location?: string;
  status: string;
  notes?: string;
  studentId?: string;
  timezone?: string;
  duration?: string;
  recurringGroupId?: string;
}
interface LessonDetailsModalProps {
  isOpen: boolean;
  lesson: LessonDetails | null;
  onClose: () => void;
  darkMode?: boolean;
  onUpdateLesson?: (lessonId: string | number, updates: any) => Promise<void | {
    deferred: boolean;
  }>;
  onDeleteLesson?: (lesson: LessonDetails) => Promise<void>;
  onReschedule?: (lesson: LessonDetails) => void;
  onDeleteButtonClick?: (lesson: LessonDetails) => boolean; // Return true if parent handled it (skip internal confirmation)
}
// Helper functions for data format conversion
const parseDisplayDate = (displayDate: string): string => {
  if (!displayDate) return '';
  try {
    if (/^\d{4}-\d{2}-\d{2}$/.test(displayDate)) {
      return displayDate;
    }
    let dateStr = displayDate;
    if (dateStr.includes(',')) {
      const parts = dateStr.split(',');
      if (parts.length >= 2) {
        dateStr = parts.slice(1).join(',').trim();
      }
    }
    const parsed = new Date(dateStr);
    if (!isNaN(parsed.getTime())) {
      const year = parsed.getFullYear();
      const month = String(parsed.getMonth() + 1).padStart(2, '0');
      const day = String(parsed.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
    return '';
  } catch (error) {
    console.error('[LessonDetailsModal] Error parsing date:', error);
    return '';
  }
};
const parseDisplayTime = (displayTime: string): string => {
  if (!displayTime) return '';
  try {
    if (/^\d{2}:\d{2}$/.test(displayTime)) {
      return displayTime;
    }
    let timeStr = displayTime;
    if (timeStr.includes('-') || timeStr.includes('–')) {
      timeStr = timeStr.split(/[-–]/)[0].trim();
    }
    const match = timeStr.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
    if (match) {
      let hours = parseInt(match[1]);
      const minutes = match[2];
      const period = match[3].toUpperCase();
      if (period === 'PM' && hours !== 12) {
        hours += 12;
      } else if (period === 'AM' && hours === 12) {
        hours = 0;
      }
      return `${String(hours).padStart(2, '0')}:${minutes}`;
    }
    return '';
  } catch (error) {
    console.error('[LessonDetailsModal] Error parsing time:', error);
    return '';
  }
};
const parseDuration = (durationStr: string): number => {
  if (!durationStr) return 45;
  if (typeof durationStr === 'number') return durationStr;
  const match = durationStr.match(/(\d+)/);
  return match ? parseInt(match[1]) : 45;
};
const formatDateForDisplay = (dateStr: string): string => {
  if (!dateStr) return '';
  try {
    const date = new Date(dateStr + 'T00:00:00');
    if (isNaN(date.getTime())) return dateStr;
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  } catch (error) {
    console.error('[LessonDetailsModal] Error formatting date:', error);
    return dateStr;
  }
};
export const LessonDetailsModal: React.FC<LessonDetailsModalProps> = ({
  isOpen,
  lesson,
  onClose,
  darkMode = false,
  onUpdateLesson,
  onDeleteLesson,
  onReschedule,
  onDeleteButtonClick
}) => {
  console.log('[LessonDetailsModal] Version: FIXED_CANCEL_CALLBACK_v2025-01-15');
  console.log('[LessonDetailsModal] Props:', {
    isOpen,
    hasLesson: !!lesson,
    hasOnUpdateLesson: !!onUpdateLesson
  });
  const navigate = useNavigate();
  const {
    currentUser
  } = useAuth();
  // ===== ALL STATE DECLARATIONS - DECLARED ONLY ONCE =====
  const [mode, setMode] = useState<'view' | 'edit'>('view');
  const [formData, setFormData] = useState({
    title: '',
    date: '',
    time: '',
    duration: '45',
    location: '',
    notes: ''
  });
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({
    x: 0,
    y: 0
  });
  const [dragStartPos, setDragStartPos] = useState({
    x: 0,
    y: 0
  });
  const [modalPosition, setModalPosition] = useState({
    top: 0,
    left: 0
  });
  const modalRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(false);
  // ===== END STATE DECLARATIONS =====
  // Check if mobile on mount and window resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 767);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  // Location options - MUST match CreateNewLessonModal exactly
  const locationOptions = [{
    value: 'Online',
    label: 'Online'
  }, {
    value: 'In-Person',
    label: 'In-Person'
  }, {
    value: "Student's Home",
    label: "Student's Home"
  }, {
    value: "Teacher's Studio",
    label: "Teacher's Studio"
  }];
  // Initialize modal position to center on mount and recalculate on mode change
  useEffect(() => {
    if (isOpen && modalRef.current && window.innerWidth >= 768) {
      const modalWidth = 512;
      const modalHeight = modalRef.current.offsetHeight || 600;
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const left = (viewportWidth - modalWidth) / 2;
      const top = (viewportHeight - modalHeight) / 2;
      setModalPosition({
        top,
        left
      });
    }
  }, [isOpen, mode]);
  // Reset scroll position when modal opens or mode changes
  useEffect(() => {
    if (isOpen && contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
  }, [isOpen, mode]);
  // Reset mode when modal opens
  useEffect(() => {
    if (isOpen) {
      setMode('view');
    }
  }, [isOpen]);
  // Stabilize drag handlers with useCallback to prevent stale closures
  const handleDrag = useCallback((e: MouseEvent) => {
    const deltaX = e.clientX - dragStartPos.x;
    const deltaY = e.clientY - dragStartPos.y;
    setDragOffset({
      x: deltaX,
      y: deltaY
    });
  }, [dragStartPos.x, dragStartPos.y]);
  const handleDragEnd = useCallback(() => {
    setModalPosition(prev => ({
      top: prev.top + dragOffset.y,
      left: prev.left + dragOffset.x
    }));
    setIsDragging(false);
    setDragOffset({
      x: 0,
      y: 0
    });
  }, [dragOffset.x, dragOffset.y]);
  // Add drag handlers
  const handleDragStart = (e: React.MouseEvent) => {
    // Disable drag on mobile
    if (window.innerWidth < 768) return;
    if (!modalRef.current) return;
    setIsDragging(true);
    setDragStartPos({
      x: e.clientX,
      y: e.clientY
    });
  };
  // Add global mouse event listeners for dragging - now with stable handlers
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleDrag);
      window.addEventListener('mouseup', handleDragEnd);
      return () => {
        window.removeEventListener('mousemove', handleDrag);
        window.removeEventListener('mouseup', handleDragEnd);
      };
    }
  }, [isDragging, handleDrag, handleDragEnd]);
  // Update form data when lesson changes
  useEffect(() => {
    if (lesson && mode === 'view') {
      const parsedDate = parseDisplayDate(lesson.date || '');
      const parsedTime = parseDisplayTime(lesson.time || '');
      const parsedDuration = parseDuration(lesson.duration || '45 minutes');
      // Normalize location to match dropdown options
      let normalizedLocation = lesson.location || 'Online';
      const locationLower = normalizedLocation.toLowerCase();
      // Map common variations to standard dropdown values
      if (locationLower.includes('online') || locationLower.includes('zoom') || locationLower.includes('virtual')) {
        normalizedLocation = 'Online';
      } else if (locationLower.includes('in-person') || locationLower === 'in person') {
        normalizedLocation = 'In-Person';
      } else if (locationLower.includes('student')) {
        normalizedLocation = "Student's Home";
      } else if (locationLower.includes('teacher') || locationLower.includes('studio')) {
        normalizedLocation = "Teacher's Studio";
      } else {
        // Default to Online if no match
        normalizedLocation = 'Online';
      }
      setFormData({
        title: lesson.title || '',
        date: parsedDate,
        time: parsedTime,
        duration: String(parsedDuration),
        location: normalizedLocation,
        notes: lesson.notes || ''
      });
      setSaveError(null);
    }
  }, [lesson, mode]);
  if (!isOpen || !lesson) return null;
  const handleEdit = () => {
    setMode('edit');
  };
  const handleCancel = () => {
    setFormData({
      title: lesson.title || '',
      date: lesson.date || '',
      time: lesson.time || '',
      duration: lesson.duration || '45 minutes',
      location: lesson.location || '',
      notes: lesson.notes || ''
    });
    setMode('view');
  };
  const handleSave = async () => {
    console.log('[SAVE] === START SAVE OPERATION ===');
    console.log('[SAVE] lesson:', lesson);
    console.log('[SAVE] formData:', formData);
    console.log('[SAVE] onUpdateLesson type:', typeof onUpdateLesson);
    if (!formData.title.trim()) {
      console.log('[SAVE] Validation failed: title');
      setSaveError('Title is required');
      toast.error('Title is required');
      return;
    }
    if (!formData.date) {
      console.log('[SAVE] Validation failed: date');
      setSaveError('Date is required');
      toast.error('Date is required');
      return;
    }
    if (!formData.time) {
      console.log('[SAVE] Validation failed: time');
      setSaveError('Time is required');
      toast.error('Time is required');
      return;
    }
    console.log('[SAVE] Validation passed');
    setIsSaving(true);
    setSaveError(null);
    try {
      console.log('[SAVE] Parsing duration from:', formData.duration);
      const durationMinutes = parseInt(formData.duration) || 45;
      console.log('[SAVE] durationMinutes:', durationMinutes);
      console.log('[SAVE] Parsing date from:', formData.date);
      const [year, month, day] = formData.date.split('-').map(Number);
      console.log('[SAVE] Parsed date:', {
        year,
        month,
        day
      });
      console.log('[SAVE] Parsing time from:', formData.time);
      const [hours, minutes] = formData.time.split(':').map(Number);
      console.log('[SAVE] Parsed time:', {
        hours,
        minutes
      });
      console.log('[SAVE] Getting timezone from lesson.timezone:', lesson?.timezone);
      const timezone = lesson.timezone || 'America/Los_Angeles';
      console.log('[SAVE] timezone:', timezone);
      console.log('[SAVE] Creating UTC base date');
      const utcBase = new Date(Date.UTC(year, month - 1, day, hours, minutes, 0, 0));
      console.log('[SAVE] utcBase:', utcBase);
      console.log('[SAVE] Creating formatters');
      const utcFormatter = new Intl.DateTimeFormat('en-US', {
        timeZone: 'UTC',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
      const tzFormatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
      console.log('[SAVE] Defining parseFormatted');
      const parseFormatted = (formatted: string) => {
        console.log('[SAVE] parseFormatted input:', formatted);
        const [datePart, timePart] = formatted.split(', ');
        const [m, d, y] = datePart.split('/').map(Number);
        const [h, min] = timePart.split(':').map(Number);
        const result = Date.UTC(y, m - 1, d, h, min, 0);
        console.log('[SAVE] parseFormatted result:', result);
        return result;
      };
      console.log('[SAVE] Calculating UTC time');
      const utcTime = parseFormatted(utcFormatter.format(utcBase));
      console.log('[SAVE] utcTime:', utcTime);
      console.log('[SAVE] Calculating TZ time');
      const tzTime = parseFormatted(tzFormatter.format(utcBase));
      console.log('[SAVE] tzTime:', tzTime);
      console.log('[SAVE] Calculating offset');
      const offsetMs = utcTime - tzTime;
      console.log('[SAVE] offsetMs:', offsetMs);
      console.log('[SAVE] Calculating correct UTC time');
      const correctUTCTime = utcBase.getTime() + offsetMs;
      console.log('[SAVE] correctUTCTime:', correctUTCTime);
      console.log('[SAVE] Creating start/end dates');
      const startAtDate = new Date(correctUTCTime);
      const startAt = startAtDate.toISOString();
      console.log('[SAVE] startAt:', startAt);
      const endAtDate = new Date(correctUTCTime + durationMinutes * 60000);
      const endAt = endAtDate.toISOString();
      console.log('[SAVE] endAt:', endAt);
      console.log('[SAVE] Creating updates object');
      const updates = {
        title: formData.title.trim(),
        startAt,
        endAt,
        durationMinutes,
        timezone,
        location: formData.location.trim(),
        notes: formData.notes.trim()
      };
      console.log('[SAVE] updates:', updates);
      console.log('[SAVE] Checking onUpdateLesson');
      if (onUpdateLesson) {
        console.log('[SAVE] Calling onUpdateLesson with lesson.id:', lesson.id);
        const result = await onUpdateLesson(lesson.id, updates);
        console.log('[SAVE] onUpdateLesson completed, result:', result);
        // If the parent returned { deferred: true }, it means the update was intercepted
        // (e.g. recurring lesson scope modal opened). Don't show success toast or switch mode.
        if (result && (result as any).deferred) {
          console.log('[SAVE] Update deferred by parent (recurring lesson). Skipping toast and mode change.');
          return;
        }
      } else {
        console.log('[SAVE] WARNING: onUpdateLesson is undefined');
      }
      console.log('[SAVE] Showing success toast');
      toast.success('Lesson updated successfully');
      console.log('[SAVE] Setting mode to view');
      setMode('view');
      console.log('[SAVE] Clearing save error');
      setSaveError(null);
      console.log('[SAVE] === SAVE COMPLETED SUCCESSFULLY ===');
    } catch (error) {
      console.error('[SAVE] === ERROR CAUGHT ===');
      console.error('[SAVE] Error object:', error);
      console.error('[SAVE] Error stack:', error instanceof Error ? error.stack : 'No stack');
      const errorMessage = error instanceof Error ? error.message : 'Failed to save changes';
      console.log('[SAVE] Setting error message:', errorMessage);
      setSaveError(errorMessage);
      toast.error(errorMessage);
    } finally {
      console.log('[SAVE] Finally block: setting isSaving to false');
      setIsSaving(false);
    }
  };
  const formatLocation = location => {
    if (!location) return 'Location not specified';
    if (location.toLowerCase() === 'online') {
      return 'Online';
    } else if (location.toLowerCase().includes('teacher')) {
      return "At Teacher's Studio";
    } else if (location.toLowerCase().includes('student')) {
      return "At Student's Location";
    } else {
      return location;
    }
  };
  const formatDate = date => {
    if (!date) return '';
    if (date.includes(',') && date.split(',').length > 1) {
      return date;
    }
    const day = date.split(',')[0] || date;
    return `${day}, September 1, 2023`;
  };
  const handleDeleteLesson = async () => {
    // If parent wants to intercept (e.g. for recurring lessons), let it handle
    if (onDeleteButtonClick && lesson && onDeleteButtonClick(lesson)) {
      return; // Parent handled it (e.g. opened recurring delete modal)
    }
    setShowDeleteConfirm(true);
  };
  const confirmDeleteLesson = async () => {
    setIsDeleting(true);
    try {
      if (onDeleteLesson) {
        await onDeleteLesson(lesson);
      } else {
        throw new Error('Cannot delete lesson: Delete function not available');
      }
    } catch (error) {
      console.error('Error in confirmDeleteLesson:', error);
      const errorMsg = error instanceof Error ? error.message : 'Failed to delete lesson';
      alert(`Failed to delete lesson: ${errorMsg}`);
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };
  const handleCancelLesson = () => {
    setShowCancelConfirm(true);
  };
  const confirmCancelLesson = async (reason: string, reasonNote?: string) => {
    // === DIAGNOSTIC LOGGING START ===
    console.log('[CANCEL] === CANCEL LESSON FLOW START ===');
    console.log('[CANCEL] lesson?.id:', lesson?.id);
    console.log('[CANCEL] typeof lesson?.id:', typeof lesson?.id);
    console.log('[CANCEL] typeof onUpdateLesson:', typeof onUpdateLesson);
    console.log('[CANCEL] cancelReason:', reason);
    console.log('[CANCEL] cancelReasonNote:', reasonNote);
    console.log('[CANCEL] Full lesson object:', lesson);
    // === DIAGNOSTIC LOGGING END ===
    // Early guards - prevent crashes if lesson or callback is missing
    if (!lesson || !lesson.id) {
      toast.error('Cannot cancel lesson: lesson data is missing');
      console.error('[CANCEL] GUARD FAILED: lesson or lesson.id is null/undefined');
      return;
    }
    if (!onUpdateLesson) {
      toast.error('Cannot cancel lesson: update function is unavailable');
      console.error('[CANCEL] GUARD FAILED: onUpdateLesson is null/undefined');
      return;
    }
    // Capture lessonId synchronously BEFORE any async operations
    const lessonId = lesson.id;
    console.log('[CANCEL] Captured lessonId:', lessonId);
    console.log('[CANCEL] typeof lessonId:', typeof lessonId);
    setIsCancelling(true);
    setShowCancelConfirm(false);
    try {
      const updates = {
        status: 'CANCELED' as const,
        cancelReason: reason,
        ...(reasonNote && {
          cancelReasonNote: reasonNote
        }),
        cancelledAt: new Date().toISOString()
      };
      console.log('[CANCEL] Updates object to send:', updates);
      console.log('[CANCEL] About to call onUpdateLesson with lessonId:', lessonId);
      // Use captured lessonId instead of lesson.id
      await onUpdateLesson(lessonId, updates);
      console.log('[CANCEL] onUpdateLesson completed successfully');
      toast.success('Lesson cancelled successfully');
      onClose();
    } catch (error) {
      console.error('[CANCEL] === ERROR CAUGHT ===');
      console.error('[CANCEL] Full error object:', error);
      console.error('[CANCEL] Error name:', error instanceof Error ? error.name : 'Unknown');
      console.error('[CANCEL] Error message:', error instanceof Error ? error.message : 'Unknown');
      console.error('[CANCEL] Error stack:', error instanceof Error ? error.stack : 'No stack');
      console.error('[CANCEL] Error constructor:', error?.constructor?.name);
      toast.error('Failed to cancel lesson. Please try again.');
    } finally {
      console.log('[CANCEL] Finally block: setting isCancelling to false');
      setIsCancelling(false);
    }
  };
  const handleSendMessage = () => {
    navigate(`/teacher-messages?student=${encodeURIComponent(lesson.studentName)}`);
  };
  const finalTop = modalPosition.top + dragOffset.y;
  const finalLeft = modalPosition.left + dragOffset.x;
  return <div className={`fixed inset-0 z-50 ${isMobile ? 'flex flex-col' : 'overflow-y-auto'}`} onClick={onClose} data-id="element-1643">
      <div className={`${isMobile ? 'flex-1 flex items-stretch justify-center px-4 py-4' : 'flex items-center justify-center min-h-screen p-4'}`} data-id="element-1644">
        {/* Background overlay */}
        <div className="fixed inset-0 bg-black/70 transition-opacity" onClick={onClose} data-id="element-1645"></div>

        {/* Modal panel */}
        <div ref={modalRef} className={`${isMobile ? 'relative w-full' : 'md:absolute'} z-20 ${darkMode ? 'bg-navy-800/90' : 'bg-white'} rounded-xl w-full max-w-lg ${isDragging ? 'cursor-move' : ''} flex flex-col`} style={{
        maxHeight: isMobile ? 'calc(100dvh - 2rem)' : '88vh',
        ...(!isMobile && window.innerWidth >= 768 ? {
          top: `${finalTop}px`,
          left: `${finalLeft}px`
        } : {}),
        userSelect: isDragging ? 'none' : 'auto',
        touchAction: 'pan-y'
      }} onClick={e => e.stopPropagation()} data-id="element-1646">
          {/* Header - flex-shrink-0 */}
          <div className={`flex items-center justify-between px-6 pt-6 pb-4 md:cursor-move flex-shrink-0`} onMouseDown={handleDragStart} title={!isMobile ? 'Drag to move' : undefined} data-id="element-1647">
            <h2 className={`text-xl font-semibold ${darkMode ? 'text-white' : 'text-gray-800'}`} data-id="element-1648">
              {mode === 'view' ? 'Lesson Details' : 'Edit Lesson'}
            </h2>
            <button onClick={onClose} className={`p-1 rounded-lg transition-colors ${darkMode ? 'hover:bg-white/10 text-gray-400 hover:text-white' : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'}`} aria-label="Close modal" data-id="element-1649">
              <XIcon className="h-5 w-5" data-id="element-1650" />
            </button>
          </div>

          {/* Scrollable content area - flex-grow overflow-y-auto */}
          <div ref={contentRef} className="flex-grow overflow-y-auto px-6 min-h-0" data-id="element-1651">
            {/* Lesson Information */}
            <div className={`mb-6 ${darkMode ? 'bg-navy-900/50' : 'bg-gray-50'} rounded-lg p-4`} data-id="element-1652">
              {mode === 'view' ? <>
                  <h4 className={`font-medium text-lg ${darkMode ? 'text-white' : 'text-gray-800'} mb-1`} data-id="element-1653">
                    {lesson.title || `${lesson.instrument} Lesson`}
                  </h4>
                  <p className={`${darkMode ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-1654">
                    with{' '}
                    <span className="font-medium" data-id="element-1655">{lesson.studentName}</span>
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4" data-id="element-1656">
                    <div className="flex items-start" data-id="element-1657">
                      <CalendarIcon className="h-5 w-5 mt-0.5 mr-3" style={{
                    color: '#3B82F6'
                  }} data-id="element-1658" />
                      <div data-id="element-1659">
                        <p className={`text-sm font-medium ${darkMode ? 'text-gray-200' : 'text-gray-700'}`} data-id="element-1660">
                          Date
                        </p>
                        <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-1661">
                          {formatDate(lesson.date)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start" data-id="element-1662">
                      <ClockIcon className="h-5 w-5 mt-0.5 mr-3" style={{
                    color: '#3B82F6'
                  }} data-id="element-1663" />
                      <div data-id="element-1664">
                        <p className={`text-sm font-medium ${darkMode ? 'text-gray-200' : 'text-gray-700'}`} data-id="element-1665">
                          Time
                        </p>
                        <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-1666">
                          {lesson.time}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start" data-id="element-1667">
                      <MapPinIcon className="h-5 w-5 mt-0.5 mr-3" style={{
                    color: '#9CA3AF'
                  }} data-id="element-1668" />
                      <div data-id="element-1669">
                        <p className={`text-sm font-medium ${darkMode ? 'text-gray-200' : 'text-gray-700'}`} data-id="element-1670">
                          Location
                        </p>
                        <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-1671">
                          {formatLocation(lesson.location)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start" data-id="element-1672">
                      <div className="h-5 w-5 mt-0.5 mr-3 flex items-center justify-center" data-id="element-1673">
                        <span className={`inline-block h-2.5 w-2.5 rounded-full ${lesson.status === 'Completed' ? 'bg-[#10B981]' : lesson.status === 'Cancelled' ? 'bg-red-500' : lesson.status === 'Missed' ? 'bg-amber-500' : 'bg-[#10B981]'}`} data-id="element-1674"></span>
                      </div>
                      <div data-id="element-1675">
                        <p className={`text-sm font-medium ${darkMode ? 'text-gray-200' : 'text-gray-700'}`} data-id="element-1676">
                          Status
                        </p>
                        <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-1677">
                          {lesson.status}
                        </p>
                      </div>
                    </div>
                  </div>
                </> : <div className="space-y-4" data-id="element-1678">
                  <div data-id="element-1679">
                    <label htmlFor="title" className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-1`} data-id="element-1680">
                      Lesson Title
                    </label>
                    <input id="title" type="text" value={formData.title} onChange={e => setFormData({
                  ...formData,
                  title: e.target.value
                })} className={`w-full rounded-md border ${darkMode ? 'bg-navy-900 border-navy-700 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'} focus:border-deep-pink shadow-sm focus:ring-0 focus:ring-offset-0 px-3 py-2.5`} placeholder="e.g. Piano Lesson, Guitar Practice" data-id="element-1681" />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-1682">
                    <div data-id="element-1683">
                      <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-1`} data-id="element-1684">
                        Date
                      </label>
                      <div className="relative" data-id="element-1685">
                        <div className="absolute inset-y-0 left-3.5 flex items-center pointer-events-none" data-id="element-1686">
                          <CalendarIcon className="h-4 w-4" style={{
                        color: '#3B82F6'
                      }} data-id="element-1687" />
                        </div>
                        <input type="date" value={formData.date} onChange={e => setFormData({
                      ...formData,
                      date: e.target.value
                    })} className={`w-full max-w-full min-w-0 pl-11 pr-4 py-2.5 rounded-lg ${darkMode ? 'bg-navy-900/50 border-navy-700/50 text-white' : 'bg-white border-gray-300 text-gray-900'} border focus:border-[#10B981] focus:ring-1 focus:ring-[#10B981]/20 shadow-sm transition-all`} data-id="element-1688" />
                      </div>
                    </div>
                    <div data-id="element-1689">
                      <label className={`block text-sm font-medium ${darkMode ? 'text-gray-200' : 'text-gray-700'} mb-1`} data-id="element-1690">
                        Time
                      </label>
                      <TimePickerDropdown value={formData.time} onChange={value => setFormData({
                    ...formData,
                    time: value
                  })} darkMode={darkMode} required={true} data-id="element-1691" />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-1692">
                    <div data-id="element-1693">
                      <label className={`block text-sm font-medium ${darkMode ? 'text-gray-200' : 'text-gray-700'} mb-1`} data-id="element-1694">
                        Duration
                      </label>
                      <DurationPickerDropdown value={parseInt(formData.duration) || 45} onChange={value => setFormData({
                    ...formData,
                    duration: String(value)
                  })} darkMode={darkMode} required={true} data-id="element-1695" />
                    </div>
                    <div data-id="element-1696">
                      <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-1`} data-id="element-1697">
                        Location
                      </label>
                      <CustomDropdown value={formData.location} options={locationOptions} onChange={value => setFormData({
                    ...formData,
                    location: value
                  })} darkMode={darkMode} icon={<MapPinIcon className="h-4 w-4 text-gray-400" data-id="element-1699" />} required={true} data-id="element-1698" />
                    </div>
                  </div>
                </div>}
            </div>

            {/* Notes Section */}
            <div className="mb-6" data-id="element-1700">
              <label className={`block text-sm font-medium ${darkMode ? 'text-gray-200' : 'text-gray-700'} mb-1`} data-id="element-1701">
                Notes (Optional)
              </label>
              {mode === 'edit' ? <div className="relative" data-id="element-1702">
                  <textarea value={formData.notes} onChange={e => setFormData({
                ...formData,
                notes: e.target.value
              })} className={`w-full px-3 py-2 pl-10 rounded-md border min-h-[80px] ${darkMode ? 'bg-navy-900/50 border-navy-700 text-white placeholder-gray-400 focus:ring-deep-pink focus:border-deep-pink' : 'bg-white text-gray-800 border-gray-300'}`} placeholder="Add any notes about this lesson..." data-id="element-1703" />
                  <div className="absolute top-3 left-3 pointer-events-none" data-id="element-1704">
                    <FileTextIcon className="h-4 w-4" style={{
                  color: '#9CA3AF'
                }} data-id="element-1705" />
                  </div>
                </div> : <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-1706">
                  {formData.notes || 'No notes for this lesson.'}
                </p>}
            </div>

            {/* Error message display */}
            {saveError && mode === 'edit' && <div className="mb-4 p-3 bg-red-500/20 border border-red-500/30 rounded-lg flex items-start" data-id="element-1707">
                <AlertCircleIcon className="h-5 w-5 text-red-500 mr-2 flex-shrink-0 mt-0.5" data-id="element-1708" />
                <div data-id="element-1709">
                  <p className={`text-sm ${darkMode ? 'text-white' : 'text-gray-800'}`} data-id="element-1710">
                    {saveError}
                  </p>
                </div>
              </div>}
          </div>

          {/* Footer - flex-shrink-0, outside scroll area */}
          <div className={`flex-shrink-0 px-6 pb-6 pt-4 md:pb-8 md:pt-5 md:border-t ${darkMode ? 'md:border-navy-700' : 'md:border-gray-200'}`} data-id="element-1711">
            {/* Action Buttons - Edit Mode */}
            {mode === 'edit' && <div className="flex justify-between items-center" data-id="element-1712">
                <button onClick={onClose} disabled={isSaving} className={`px-4 py-2 rounded-md border-2 transition-colors flex items-center ${isSaving ? 'border-gray-400 text-gray-400 cursor-not-allowed' : darkMode ? 'border-gray-500 text-gray-300 hover:bg-gray-500/10' : 'border-gray-400 text-gray-600 hover:bg-gray-400/5'}`} data-id="element-1713">
                  Cancel
                </button>
                <button onClick={handleSave} disabled={isSaving} className={`px-4 py-2 rounded-md border-2 transition-colors flex items-center ${isSaving ? 'border-gray-400 text-gray-400 cursor-not-allowed' : darkMode ? 'border-[#22C55E] text-[#22C55E] hover:bg-[#22C55E]/10' : 'border-[#22C55E] text-[#22C55E] hover:bg-[#22C55E]/5'}`} data-id="element-1714">
                  {isSaving ? <>
                      <svg className="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-1715">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-1716"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-1717"></path>
                      </svg>
                      Saving...
                    </> : 'Save Changes'}
                </button>
              </div>}

            {/* Action Buttons - View Mode */}
            {mode === 'view' && <div className="flex justify-between items-end gap-3" data-id="element-1718">
                <div className="flex gap-3" data-id="element-1719">
                  <button onClick={handleDeleteLesson} className={`border-2 py-2 px-4 rounded-lg transition-colors flex items-center ${darkMode ? 'border-red-500/50 text-red-400 hover:bg-red-500/10' : 'border-red-500 text-red-600 hover:bg-red-50'}`} data-id="element-1720">
                    <Trash2Icon className="h-4 w-4 mr-2" data-id="element-1721" />
                    Delete
                  </button>
                  {lesson.status !== 'CANCELED' && lesson.status !== 'Cancelled' && lesson.status !== 'COMPLETED' && lesson.status !== 'Completed' && <button onClick={handleCancelLesson} className={`border-2 py-2 px-4 rounded-lg transition-colors flex items-center ${darkMode ? 'border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/10' : 'border-yellow-500 text-yellow-600 hover:bg-yellow-50'}`} data-id="element-1722">
                        <XIcon className="h-4 w-4 mr-2" data-id="element-1723" />
                        Cancel
                      </button>}
                </div>
                <div className="flex flex-col gap-3 items-stretch" data-id="element-1724">
                  <button onClick={handleEdit} className={`border-2 py-2 px-4 rounded-lg transition-colors ${darkMode ? 'border-[#22C55E]/50 text-[#22C55E] hover:bg-[#22C55E]/10' : 'border-[#22C55E] text-[#22C55E] hover:bg-[#22C55E]/5'}`} title="Edit lesson" data-id="element-1725">
                    Edit Lesson
                  </button>
                </div>
              </div>}
          </div>
        </div>

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-id="element-1726">
            <div className="absolute inset-0 bg-black/70" onClick={() => setShowDeleteConfirm(false)} data-id="element-1727"></div>
            <div className={`w-full max-w-md rounded-lg overflow-hidden shadow-xl z-10 ${darkMode ? 'bg-navy-800' : 'bg-white'}`} data-id="element-1728">
              <div className={`px-6 py-4 ${darkMode ? 'bg-navy-700' : 'bg-blue-600'} text-white`} data-id="element-1729">
                <h3 className="text-lg font-medium" data-id="element-1730">Delete Lesson</h3>
              </div>
              <div className={`p-6 ${darkMode ? 'text-white' : 'text-gray-800'}`} data-id="element-1731">
                <div className="flex items-start mb-4" data-id="element-1732">
                  <div className={`p-2 rounded-full ${darkMode ? 'bg-red-900/30' : 'bg-red-100'} mr-3 flex-shrink-0`} data-id="element-1733">
                    <AlertTriangleIcon className={`h-5 w-5 ${darkMode ? 'text-red-400' : 'text-red-600'}`} data-id="element-1734" />
                  </div>
                  <div data-id="element-1735">
                    <p className="font-medium mb-2" data-id="element-1736">
                      Are you sure you want to delete this lesson?
                    </p>
                    <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-1737">
                      This action cannot be undone. This lesson will be
                      permanently deleted from your calendar.
                    </p>
                  </div>
                </div>
                <div className="flex justify-end space-x-3 mt-6" data-id="element-1738">
                  <button onClick={() => setShowDeleteConfirm(false)} className={`px-4 py-2 rounded-lg border ${darkMode ? 'border-gray-600 text-gray-300 hover:bg-navy-700' : 'border-gray-300 text-gray-700 hover:bg-gray-100'} transition-colors`} data-id="element-1739">
                    Cancel
                  </button>
                  <button onClick={confirmDeleteLesson} disabled={isDeleting} className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center" data-id="element-1740">
                    {isDeleting ? 'Deleting...' : 'Delete Lesson'}
                  </button>
                </div>
              </div>
            </div>
          </div>}

        {/* Cancel Lesson Modal */}
        <CancelLessonModal isOpen={showCancelConfirm} onClose={() => setShowCancelConfirm(false)} onConfirm={confirmCancelLesson} lessonTitle={lesson?.title || 'Lesson'} studentName={lesson?.studentName} darkMode={darkMode} data-id="element-1741" />
      </div>
      {/* Add mobile-specific CSS for date input */}
      <style jsx global data-id="element-1742">{`
        @media (max-width: 767px) {
          input[type='date'] {
            min-width: 0 !important;
            max-width: 100% !important;
            -webkit-appearance: none !important;
            -moz-appearance: textfield !important;
            appearance: none !important;
          }
        }
      `}</style>
    </div>;
};